import { z } from 'zod';

// Define the schemas
export const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters')
});

// Type for validation result
export type ValidationResult<T> = 
  | { success: true; data: T; errors?: never }
  | { success: false; errors: { message: string }[] };

// Generic validation function that returns consistent error structure
export const validateData = <T>(schema: z.ZodSchema<T>, data: unknown): ValidationResult<T> => {
  try {
    const validData = schema.parse(data);
    return { success: true, data: validData };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        errors: error.errors.map(err => ({ message: err.message }))
      };
    }
    return {
      success: false,
      errors: [{ message: 'An unknown error occurred during validation' }]
    };
  }
};

/**
 * Sanitize user input to prevent XSS attacks
 * @param input String input to sanitize
 * @returns Sanitized string
 */
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  
  // Replace potentially dangerous characters
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/`/g, '&#096;');
};
